import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RoleDto, UserDto } from '@thebb/shared';
import { get, patch, post } from '../api';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';

type Paged<T> = { items: T[]; total: number };

export function UsersPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<UserDto | 'new' | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const users = useQuery({ queryKey: ['users', q], queryFn: () => get<Paged<UserDto>>(`/users?perPage=100&q=${encodeURIComponent(q)}`) });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => get<{ roles: RoleDto[] }>('/roles') });

  const passwordLink = useMutation({
    mutationFn: (id: string) => post<{ setPasswordToken: string }>(`/users/${id}/password-link`),
    onSuccess: (r) => setLink(`${window.location.origin}/set-password?token=${r.setPasswordToken}`),
  });

  return (
    <div>
      <PageTitle
        title="Brugere"
        actions={
          <>
            <input className="input w-56" placeholder="Søg navn eller e-mail" value={q} onChange={(e) => setQ(e.target.value)} />
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Ny bruger
            </button>
          </>
        }
      />
      {link && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <div className="mb-1 font-medium">Link til at vælge adgangskode (gyldigt 48 timer, vises kun én gang):</div>
          <code className="break-all">{link}</code>
          <button className="btn ml-2" onClick={() => setLink(null)}>
            Luk
          </button>
        </div>
      )}
      {users.isLoading ? (
        <Spinner />
      ) : (
        <table className="table card p-0">
          <thead>
            <tr>
              <th>Navn</th>
              <th>E-mail</th>
              <th>Roller</th>
              <th>Status</th>
              <th>2FA</th>
              <th>Sidst set</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.data?.items.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td className="space-x-1">
                  {u.roles.map((r) => (
                    <Badge key={r}>{r}</Badge>
                  ))}
                </td>
                <td>
                  <Badge tone={u.status === 'active' ? 'green' : 'red'}>{u.status === 'active' ? 'aktiv' : 'deaktiveret'}</Badge>
                </td>
                <td>{u.totpEnabled ? 'ja' : 'nej'}</td>
                <td>{fmtTime(u.lastLoginAt)}</td>
                <td className="space-x-2 whitespace-nowrap">
                  <button className="btn" onClick={() => setEditing(u)}>
                    Redigér
                  </button>
                  <button className="btn" onClick={() => passwordLink.mutate(u.id)}>
                    Adgangskode-link
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <UserModal
          user={editing === 'new' ? null : editing}
          roles={roles.data?.roles ?? []}
          onClose={() => setEditing(null)}
          onSaved={(token) => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ['users'] });
            if (token) setLink(`${window.location.origin}/set-password?token=${token}`);
          }}
        />
      )}
    </div>
  );
}

function UserModal({ user, roles, onClose, onSaved }: { user: UserDto | null; roles: RoleDto[]; onClose: () => void; onSaved: (token: string | null) => void }) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [locale, setLocale] = useState<'da' | 'en'>(user?.locale ?? 'da');
  const [status, setStatus] = useState(user?.status ?? 'active');
  const [selected, setSelected] = useState<string[]>(user?.roles ?? []);
  const [printers, setPrinters] = useState(user?.printers ?? { labelPrinterId: null, smallLabelPrinterId: null, documentPrinterId: null });
  const [error, setError] = useState<unknown>(null);
  const printerOptions = useQuery({ queryKey: ['user-printer-options'], queryFn: () => get<{ id: string; label: string }[]>('/users/printer-options') });

  const save = useMutation({
    mutationFn: async () => {
      if (user) {
        await patch(`/users/${user.id}`, { name, locale, status, roles: selected, printers });
        return null;
      }
      const r = await post<{ setPasswordToken: string | null }>('/users', { name, email, locale, roles: selected, printers });
      return r.setPasswordToken;
    },
    onSuccess: (token) => onSaved(token),
    onError: setError,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Modal title={user ? `Redigér ${user.email}` : 'Ny bruger'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <ErrorBox error={error} />
        <div>
          <label className="label">Navn</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        {!user && (
          <div>
            <label className="label">E-mail</label>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Sprog</label>
            <select className="input" value={locale} onChange={(e) => setLocale(e.target.value as 'da' | 'en')}>
              <option value="da">Dansk</option>
              <option value="en">English</option>
            </select>
          </div>
          {user && (
            <div>
              <label className="label">Status</label>
              <select className="input" value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'disabled')}>
                <option value="active">Aktiv</option>
                <option value="disabled">Deaktiveret</option>
              </select>
            </div>
          )}
        </div>
        <div>
          <label className="label">Roller</label>
          <div className="grid grid-cols-2 gap-1">
            {roles.map((r) => (
              <label key={r.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(r.key)}
                  onChange={(e) => setSelected(e.target.checked ? [...selected, r.key] : selected.filter((k) => k !== r.key))}
                />
                {r.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="label">Printere til julepakning</label>
          <div className="space-y-2">
            {([['labelPrinterId', 'Labelprinter 102 × 191 mm (kasser og fragt)'], ['smallLabelPrinterId', 'Labelprinter 76 × 50 mm (medarbejderlabels)'], ['documentPrinterId', 'A4-printer']] as const).map(([k, label]) => (
              <label key={k} className="block text-sm">
                <span className="text-xs text-muted">{label}</span>
                <select className="input" value={printers[k] ?? ''} onChange={(e) => setPrinters({ ...printers, [k]: e.target.value || null })}>
                  <option value="">Planens standard</option>
                  {(printerOptions.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            Annullér
          </button>
          <button className="btn btn-primary" disabled={save.isPending || selected.length === 0}>
            Gem
          </button>
        </div>
      </form>
    </Modal>
  );
}
